import { FormikProps } from 'formik';
import { SwalAlertContent } from 'pages/detailed-consultation/components';
import { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { useSelector } from 'react-redux';
import { RootState } from 'shared/store/store';
import { debounceFunction, removeHTMLTagsFromText } from 'shared/utils';
import Swal from 'sweetalert2';
import * as Yup from "yup";
import { INoAgreementBalance } from '../model/INoAgreementBalance';
import { getAttachDocument, getAuditConsolidate, postAttachDocument, postAuditConsolidate } from 'shared/api/services';
import { IPostAuditConsolidate } from '../model/IAuditConsolidateGloses';
import { CreditNoteType } from 'pages/detailed-consultation/Models/ECreditNoteType';
import { useDetailedConsultation } from 'pages/detailed-consultation/hooks/useDetailedConsultation';
import { valuesPayment } from 'shared/models/store/IAuditDetails';
import { activities } from 'shared/models/lineStates';

interface Props {
    hideModal: (hide: boolean) => void;
    selectedDocument: string;
    closeModal: () => void;
}

export const useFormNoAgreementBalance = ({ hideModal, selectedDocument, closeModal }: Props) => {

    const supportedFormats = ['pdf'];

    const validationSchema = () => Yup.object().shape({
        managementDetail: Yup.string()
            .test('minLength', value => {
                if (value) {
                    return removeHTMLTagsFromText(value).length >= 200 || removeHTMLTagsFromText(value).length < 0;
                }
                return true;
            })
            .test('maxLength', value => {
                if (value) {
                    return removeHTMLTagsFromText(value).length <= 5000;
                }
                return true;
            }),
        uploadResponse: Yup.mixed()
            .test('fileFormat', value => {
                if (value?.file) {                   
                    return supportedFormats.includes(value.file.name.split('.').pop());
                }
                return true;
            })
            .test('fileSize', value => {
                if (value?.file) {
                    return value.file.size <= 10485760;
                }
                return true;
            }),
        acceptedValue: Yup.string().required().max(100),
        authorizedValue: Yup.string().required().max(15),
    });
            

    const { dataGeneralParameters } = useSelector((state: RootState) => state.invoiceData);
    const { valuesPaymentConsolidate, bill, billingState } = useSelector((state: RootState) => state.auditDetails);
    
    const { content } = dataGeneralParameters[0];
    const formRef = useRef<FormikProps<any>>(null);
    const [acceptedValueHasValue, setAcceptedValueHasValue] = useState<number | undefined>(undefined);
    const [formIsValid, setFormIsValid] = useState<boolean>(false);
    const [readOnly, setReadOnly] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [initialValues, setInitialValues] = useState<INoAgreementBalance>({
        managementDetail: '<p><br></p>',
        uploadResponse: undefined,
        acceptedValue: '',
        authorizedValue: ''
    });

    
    const { callGetDetailedDocument } = useDetailedConsultation();

    const showConfirmAcceptation = () => {
        hideModal(true);
        const modalComponent = document.createElement("div");

        createRoot(modalComponent).render(<SwalAlertContent
            description={`¿Confirma que desea Guardar la gestión realizada con el saldo en No Acuerdo ${bill}?`}
            useCheck
            checkText='Confirmo que deseo Guardar la Gestión del Saldo en No Acuerdo'
            onCancel={() => { Swal.clickCancel(); hideModal(false); }}
            onConfirm={() => {
                Swal.clickCancel();
                postAuditConsolidateGloses();
            }}
            textBtn='Guardar'
        />);

        Swal.fire({
            title: "¡Atención!",
            icon: "warning",
            html: modalComponent,
            showCancelButton: false,
            showConfirmButton: false,
            willClose: () => { hideModal(false) }
        });
    }

    const verifyIfAcceptedHasValue = (fieldName: string, currentValue: string) => {
        if (fieldName === 'acceptedValue' && currentValue !== '0') {
            debounceFunction(() => {
                setAcceptedValueHasValue(Number(currentValue));
            })
        }
    }

    const checkIfFormIsValid = (formik: FormikProps<any>) => {
        setTimeout(() => {
            setFormIsValid(formik.isValid);
        });
    }

    const downloadFile = (file?: File) => {
        if (!readOnly || file?.name === undefined) return;

        const url = window.URL.createObjectURL(file);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.name;
        a.click();
    }

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, fieldName: string) => {
        const file = event.target.files![0];
      
        formRef.current?.setFieldValue(fieldName, {
          id: null,
          file: file,
        });
      
        if(supportedFormats.includes(file?.name.split('.').pop() ?? '') ){
            uploadAttachedDocument(file);
        }
      
        if (event.target) {
          event.target.value = '';
        }
      };

    const uploadAttachedDocument = (file: File) => {
        setIsLoading(true);

        postAttachDocument(selectedDocument.toString(), file)
            .then((uploadedFileId) => {
                const currentValue = formRef.current?.values['uploadResponse'];

                formRef.current?.setFieldValue('uploadResponse', {
                    ...currentValue,
                    id: uploadedFileId
                });
            })
            .finally(() => { setIsLoading(false) })
        
    }

    const postAuditConsolidateGloses = () => {
        setIsLoading(true);

        const request: IPostAuditConsolidate = {
            attachmentId: formRef.current?.values['uploadResponse']?.id,
            inNoAgreeDetail: formRef.current?.values['managementDetail'],
            acceptedValue: formRef.current?.values['acceptedValue'],
            authorizedPaymentValue: formRef.current?.values['authorizedValue'],
            type: CreditNoteType.NoAgree,
            noAgreement: true
        }

        postAuditConsolidate(selectedDocument.toString(), request)
            .then(() => {
                callGetDetailedDocument(selectedDocument.toString());
                closeModal();
            })
            .finally(() => { 
                setIsLoading(false) 
            })


    }

    const getAuditConsolidateGloses = () => {
        setIsLoading(true);

        getAuditConsolidate(selectedDocument.toString())
            .then(async ({ inNoAgreeDetail, attachment }) => {
                let newFile: File | undefined = undefined;

                if (attachment?.id) {
                    newFile = await getAttachDocument(selectedDocument.toString(), attachment.id, attachment.originalName);
                }
                
                setInitialValues({
                    managementDetail: inNoAgreeDetail || '<p><br></p>',
                    uploadResponse: {
                        id: undefined,
                        file: newFile
                    },
                    acceptedValue: content.acceptedValue || content.acceptedValue === 0 ? content.acceptedValue.toString() : '',
                    authorizedValue: readOnly ? content.authorizedPaymentValue || content.authorizedPaymentValue === 0 ? content.authorizedPaymentValue.toString() : '' : ''
                })
            })
            .finally(() => { setIsLoading(false) })
    }

    useEffect(() => { getAuditConsolidateGloses() }, [])

    useEffect(() => {
        setReadOnly( valuesPaymentConsolidate?.initialValues?.consolidatedInNoAgreementValue?.value <= 0 || billingState.code === activities.facturaConAutorizacionPagos)
    }, []);


    return {
        initialValues,
        validationSchema,
        acceptedValueHasValue,
        formIsValid,
        formRef,
        readOnly,
        isLoading,
        showConfirmAcceptation,
        checkIfFormIsValid,
        verifyIfAcceptedHasValue,
        downloadFile,
        handleFileChange
    }
}
